import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx-ugnis';
import { getDropZoneBorder } from '../utils/getDropZoneBorder';
import { useRsi } from '../../../hooks/useRsi';
import { readFileAsync } from '../utils/readFilesAsync';
import { Box, bpDesignBaseTheme, Button, styled, Typography } from '@beeline.com/bp-design-system';

type DropZoneProps = {
  onContinue: (data: XLSX.WorkBook, file: File) => void;
  isLoading: boolean;
};

export const DropZone = ({ onContinue, isLoading }: DropZoneProps) => {
  const { translations, maxFileSize, dateFormat, parseRaw } = useRsi();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxFiles: 1,
    maxSize: maxFileSize,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv']
    },
    onDropRejected: (fileRejections) => {
      setLoading(false);
      for (const fileRejection of fileRejections) {
        enqueueSnackbar(
          <>
            {fileRejection.file.name} {translations.uploadStep.dropzone.errorToastDescription}
            <br />
            {fileRejection.errors[0]?.message || ''}
          </>,
          {
            variant: 'error'
          }
        );
      }
    },
    onDropAccepted: async ([file]: File[]) => {
      setLoading(true);
      const arrayBuffer = await readFileAsync(file as File);
      const workbook = XLSX.read(arrayBuffer, {
        cellDates: true,
        dateNF: dateFormat,
        raw: parseRaw,
        dense: true
      });
      setLoading(false);
      onContinue(workbook, file as File);
    }
  });

  return (
    <Box
      {...getRootProps()}
      contentEditable={undefined}
      display="flex"
      flex={1}
      flexDirection="column"
      flexGrow={2}
      justifyContent="center"
      style={getDropZoneBorder(bpDesignBaseTheme.tokens.source.paletteBlue40)}
      width="100%"
    >
      <StyledInnerDropZone>
        <input {...(getInputProps() as React.InputHTMLAttributes<HTMLInputElement>)} data-test="rsi-dropzone" />
        {isDragActive ? (
          <Typography>{translations.uploadStep.dropzone.activeDropzoneTitle}</Typography>
        ) : loading || isLoading ? (
          <Typography>{translations.uploadStep.dropzone.loadingTitle}</Typography>
        ) : (
          <StyleContent>
            <StyledButton onClick={open}>
              <FileUploadIcon />
            </StyledButton>
            <Typography onClick={open} variant="label1">
              {translations.uploadStep.dropzone.title}
            </Typography>
            <Typography pt={3} variant="body1">
              {translations.uploadStep.dropzone.subTitle}
            </Typography>
            <StyledRecommendedText variant="body2" pt={3}>
              {translations.uploadStep.dropzone.recommendedMaximumRows}
            </StyledRecommendedText>
          </StyleContent>
        )}
      </StyledInnerDropZone>
    </Box>
  );
};

const BORDER_PADDING = 72;

const StyledInnerDropZone = styled('div')`
  // When a lot of columns are displayed the Select file button
  // can be displayed off screen. This will ensure it is always
  // visible.
  max-width: calc(100vw - ${BORDER_PADDING}px);
  text-align: center;
  width: 100%;
`;

const StyleContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledButton = styled(Button)`
  margin-bottom: 2rem;

border-radius: 50%;
  height: 68px;
  width: 68px; 
  transition: none; 

  :hover {
    border-radius: 50%;
    height: 68px;
    width: 68px;
  }
`;

const StyledRecommendedText = styled(Typography)`
  max-width: 320px;
`;

function FileUploadIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="56"
      height="56"
      focusable="false"
      role="presentation"
      viewBox="0 0 56 56"
    >
      <g fill="none" fillRule="evenodd">
        <circle cx="28" cy="28" r="22" fill="#FFF" />
        <path
          fill="#005DBA"
          fillRule="nonzero"
          d="M28 52C14.745 52 4 41.255 4 28S14.745 4 28 4s24 10.745 24 24-10.745 24-24 24zm0-2c12.15 0 22-9.85 22-22S40.15 6 28 6 6 15.85 6 28s9.85 22 22 22zm-6.732-22.664a.506.506 0 0 1-.71-.003l-.701-.7a.495.495 0 0 1-.003-.71l7.777-7.779a.498.498 0 0 1 .352-.144.48.48 0 0 1 .356.144l7.778 7.778a.506.506 0 0 1-.003.71l-.7.701a.495.495 0 0 1-.711.003L29 21.634V37.26c0 .273-.226.5-.505.5h-.99a.495.495 0 0 1-.505-.5V21.604l-5.732 5.732z"
        />
      </g>
    </svg>
  );
}
